import React, { FC, Fragment, useState } from "react";
import { useTranslation } from "Providers/TranslationProvider";
import { ScheduleEntryType, ScheduleEntryTypeCategory } from "domain-entities";
import { DialogContent, Button, Box, makeStyles } from "@material-ui/core";
import {
	useTranslateScheduleEntryType,
	getScheduleEntryTypeIcon,
	DEFAULT_SCHEDULE_ENTRY_TYPE_ICON,
	useTranslateScheduleEntryTypeCategory,
} from "Components/ScheduleEntryType/ScheduleEntryTypeHelper";
import { ModalController } from "Components/Modal/Modal";
import { v4 } from "uuid";
import {
	DEFAULT_COLOR_TAG,
	getColorHex,
	getTextColorFromBackgroundColor,
} from "Helpers/ColorHelper";
import { px } from "Configs/theme";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import ModalTitle from "Components/Modal/ModalTitle";
import { useApp } from "Providers/AppProvider";
import { useScheduleEntryTypes } from "./hooks/useScheduleEntryTypes";

interface Props {
	scheduleEntryTypeMC: ModalController<ScheduleEntryType>;
	close: () => void;
}

const ScheduleEntryTypesForm: FC<Props> = (props) => {
	const classes = useStyles();
	const { isNative } = useApp();
	const { t } = useTranslation();
	const translateScheduleEntryType = useTranslateScheduleEntryType();
	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);
	const translateScheduleEntryTypeCategory = useTranslateScheduleEntryTypeCategory();
	const [selectedCategory, setSelectedCategory] = useState(
		ScheduleEntryTypeCategory.USER
	);

	function createNewScheduleEntryType() {
		props.scheduleEntryTypeMC.open(
			{
				id: v4(),
				category: selectedCategory,
				icon: DEFAULT_SCHEDULE_ENTRY_TYPE_ICON,
				isDefault: false,
				isDeleted: false,
				name: "",
				colorTag: DEFAULT_COLOR_TAG,
			},
			true
		);
	}

	function renderScheduleEntryTypes() {
		return scheduleEntryTypes
			.filter(
				(scheduleEntryType) => scheduleEntryType.category === selectedCategory
			)
			.map((scheduleEntryType) => {
				if (!scheduleEntryType.isDeleted) {
					const colorHex = getColorHex(scheduleEntryType.colorTag);

					return (
						<Box
							key={scheduleEntryType.id}
							className={classes.scheduleEntryType}
							style={{
								backgroundColor: getColorHex(scheduleEntryType.colorTag),
								color: getTextColorFromBackgroundColor(colorHex, true),
							}}
							onClick={() => props.scheduleEntryTypeMC.open(scheduleEntryType)}
						>
							<Box mr={px.l}>
								{getScheduleEntryTypeIcon(scheduleEntryType.icon, px.xl)}
							</Box>
							{translateScheduleEntryType(scheduleEntryType)}
						</Box>
					);
				} else {
					return null;
				}
			});
	}

	return (
		<Fragment>
			<ModalTitle
				close={props.close}
				title={t("ScheduleEntryTypesForm_Title_Edit")}
			>
				<Button
					autoFocus={!isNative}
					variant="contained"
					color="primary"
					onClick={createNewScheduleEntryType}
				>
					{t("ScheduleEntryTypesForm_Button_Create")}
				</Button>
			</ModalTitle>
			<DialogContent>
				<Box display="flex" justifyContent="center" mb={px.xl}>
					<ToggleButtonGroup
						exclusive
						value={selectedCategory}
						onChange={(_, next: ScheduleEntryTypeCategory | null) =>
							next !== null && setSelectedCategory(next)
						}
					>
						{[
							ScheduleEntryTypeCategory.USER,
							ScheduleEntryTypeCategory.RESOURCE,
						].map((category, index) => (
							<ToggleButton key={index} value={category}>
								{translateScheduleEntryTypeCategory(category)}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>
				{renderScheduleEntryTypes()}
			</DialogContent>
		</Fragment>
	);
};

export default ScheduleEntryTypesForm;

const useStyles = makeStyles({
	scheduleEntryType: {
		cursor: "pointer",
		textTransform: "uppercase",
		display: "flex",
		alignItems: "center",
		height: 50,
		padding: `0 ${px.l}`,
		borderRadius: px.xs,
		fontSize: "14px",
		fontWeight: "bold",
		marginBottom: px.l,
		"&:hover": {
			opacity: 0.9,
		},
	},
});
